import React, { useState } from "react";
import { Printer, ArrowLeft, FileDown } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { API_URL } from "@/lib/api";
import { FICHES, APPLICATIONS, fmt, euros } from "@/lib/cee";

const Row = ({ label, value, total = false }) => (
  <tr className={total ? "total" : ""}>
    <td>{label}</td>
    <td className="num">{value}</td>
  </tr>
);

const DevisDocument = ({ dossier, onClose }) => {
  const { totals, operationResults } = dossier;
  const ops = dossier.operations || [];
  const validite = parseFloat(dossier.devisValidite) || 30;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/60 backdrop-blur-sm">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[var(--line)] bg-[var(--panel)] px-5 py-3">
        <button data-testid="devis-back-btn" onClick={onClose} className="btn-ghost">
          <ArrowLeft size={15} /> Retour au calculateur
        </button>
        <div className="flex items-center gap-2.5">
          <a data-testid="devis-pdf-btn" href={`${API_URL}/dossiers/${dossier.id}/devis.pdf`} download className="btn-ghost">
            <FileDown size={15} /> Télécharger PDF
          </a>
          <button data-testid="devis-print-btn" onClick={() => window.print()} className="btn-primary">
            <Printer size={15} /> Imprimer l'estimation
          </button>
        </div>
      </div>

      <div className="devis-print mx-auto my-8 w-[min(860px,94vw)] rounded-2xl bg-white p-8 text-slate-900 shadow-2xl sm:p-12">
        {/* En-tête */}
        <div className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-slate-900 pb-6">
          <div className="flex items-center gap-3">
            <LogoMark size={40} />
            <div>
              <div className="font-display text-lg font-extrabold tracking-tight text-slate-900">THIONNET SERVICE INDUSTRIE</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-sky-700">
                Spécialiste CEE — moteurs & variateurs industriels
              </div>
              {(dossier.entTel || dossier.entEmail) && (
                <div className="mt-1 text-xs text-slate-500">
                  {[dossier.entTel && `Tél. : ${dossier.entTel}`, dossier.entEmail && `Email : ${dossier.entEmail}`]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="font-display text-2xl font-extrabold tracking-tight">Estimation Prime CEE</div>
            <div className="mt-1 font-mono text-xs text-slate-500">N° {dossier.devisNum || "—"}</div>
            <div className="text-xs text-slate-500">Date : {new Date().toLocaleDateString("fr-FR")}</div>
            <div className="text-xs text-slate-500">Validité : {validite} jours</div>
          </div>
        </div>

        {/* Client */}
        <div className="mt-6 grid gap-x-10 gap-y-1 text-sm sm:grid-cols-[180px_1fr]">
          <div className="text-slate-500">Bénéficiaire</div>
          <div className="font-semibold">{dossier.clientNom || <em className="text-slate-400">Nom du bénéficiaire à compléter</em>}</div>
          {dossier.benefSiren?.replace(/\D/g, "").length === 9 && (
            <>
              <div className="text-slate-500">SIREN</div>
              <div className="font-mono">{dossier.benefSiren}</div>
            </>
          )}
          {dossier.benefAdresse && (
            <>
              <div className="text-slate-500">Adresse du bénéficiaire</div>
              <div>{dossier.benefAdresse}</div>
            </>
          )}
          <div className="text-slate-500">Adresse des travaux</div>
          <div>{dossier.adresseTravaux || <em className="text-slate-400">à compléter</em>}</div>
        </div>

        {/* Opérations */}
        <div className="mt-8 space-y-6">
          {ops.map((op, idx) => {
            const r = operationResults[idx] || {};
            const fiche = FICHES[op.fiche] || {};
            return (
              <div key={idx} className="rounded-xl border border-slate-200 p-5">
                <h4 className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">
                  Opération {idx + 1} — fiche {op.fiche}{(op.ficheVersion || fiche.version) ? ` · version ${op.ficheVersion || fiche.version}` : ""}
                </h4>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">{fiche.designation}</p>
                <table className="mt-4 w-full text-sm" style={{ borderCollapse: "collapse" }}>
                  <tbody>
                    {[
                      ["Marque", op.marque || "—"],
                      ["Référence commerciale", op.reference || "—"],
                      ["Type d'application", op.fiche === "IND-UT-132" ? "—" : APPLICATIONS[op.application] || op.application],
                      ["Puissance nominale totale installée", `${fmt(r.puissance, 1)} kW`],
                      ["Nombre de moteurs concernés", op.nbMoteurs ? fmt(op.nbMoteurs) : "—"],
                      ["Durée de vie conventionnelle", r.dureeVie ? `${r.dureeVie} ans` : "—"],
                      ["Volume généré (MWh cumac)", fmt(r.mwhCumac, 1)],
                      ["Prime CEE de cette opération", euros(Math.round(r.prime || 0))],
                    ].map(([label, value], i, arr) => (
                      <tr key={label} className={i >= arr.length - 2 ? "font-semibold" : ""} style={{ borderTop: "1px solid #e2e8f0" }}>
                        <td className="py-1.5 pr-4 text-slate-600">{label}</td>
                        <td className="py-1.5 text-right font-mono">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Totaux */}
        <table className="mt-8 w-full text-sm" style={{ borderCollapse: "collapse" }}>
          <tbody>
            {[
              ["Nombre d'opérations", fmt(totals.nbOperations)],
              ["Volume total généré (MWh cumac)", fmt(totals.mwhCumacTotal, 1)],
              ["Montant total de la prime CEE proposée", euros(Math.round(totals.primeNette))],
            ].map(([label, value], i, arr) => (
              <tr key={label} className={i === arr.length - 1 ? "font-bold" : ""} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td className="py-2 pr-4 text-slate-600">{label}</td>
                <td className="py-2 text-right font-mono">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-4 text-[11px] italic leading-relaxed text-slate-500">
          Montant estimé sur la base des informations techniques renseignées ci-dessus, opération par opération ; à confirmer après
          validation définitive du dossier CEE.
        </p>

        {/* Mentions CEE */}
        <div className="mt-8 rounded-xl bg-slate-50 p-5">
          <h4 className="font-display text-[11px] font-bold uppercase tracking-wide text-slate-700">
            Mentions relatives aux certificats d'économies d'énergie
          </h4>
          <p className="mt-2 text-[10.5px] leading-relaxed text-slate-600">
            Les opérations ci-dessus sont éligibles au dispositif des certificats d'économies d'énergie (CEE), au titre de la ou des
            fiches IND-UT-102 et/ou IND-UT-114 mentionnées pour chacune. Le montant de la prime CEE indiqué ci-dessus est estimatif ; il
            sera confirmé à l'issue du contrôle de conformité du dossier par l'obligé partenaire, sur la base des justificatifs de
            réalisation de chaque opération (facture, attestation sur l'honneur et preuve de réalisation conformes aux exigences de la
            fiche applicable à chacune, en vigueur). Conformément à la réglementation, les travaux ne doivent pas débuter avant la date
            d'engagement de l'opération concernée, déterminée séparément selon les modalités applicables.
          </p>
        </div>

        {/* Conditions */}
        <div className="mt-4 rounded-xl bg-slate-50 p-5">
          <h4 className="font-display text-[11px] font-bold uppercase tracking-wide text-slate-700">Conditions</h4>
          <ul className="mt-2 list-inside list-disc space-y-1.5 text-[10.5px] leading-relaxed text-slate-600">
            <li>
              Estimation établie à titre indicatif et informatif, sans valeur contractuelle, valable {validite} jours à compter de sa
              date d'émission.
            </li>
            <li>
              Document établi à titre indicatif et informatif ; il ne constitue pas un engagement contractuel. Les montants de prime CEE
              restent soumis à la vérification définitive des conditions d'éligibilité de la ou des fiches en vigueur au moment de
              l'engagement de chaque opération.
            </li>
          </ul>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-4 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
          THIONNET SERVICE INDUSTRIE — Estimation Prime CEE générée depuis le portail client
        </div>
      </div>
    </div>
  );
};

export default DevisDocument;
